import React, { useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, ActivityIndicator, Pressable,
} from 'react-native';
import Modal from 'react-native-modal';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSelector } from 'react-redux';
import editArea from '../../../../../requests/Locations/Areas/EditArea';
import deleteArea from '../../../../../requests/Locations/Areas/DeleteArea';
import {
  DELETE_AREA_FAILURE, EDIT_AREA_FAILURE, PROVIDE_CAPACITY,
} from '../../../../../constants/AlertMessages';
import { Area, LocationSubtypeTitles } from '../../../../../components/Map/SetFloorsModal/FloorItem';
import asNumber from '../../../../../components/AsNumber';
import nameRegex, { externalIdRegex, integerRegex } from '../../../../../components/Regex';
import Style from '../../../../../constants/Style';
import PressableStyled from '../../../../../components/PressableStyled';
import CloseModalIcon from '../../../../../components/CloseModalIcon';
import CustomTextField from '../../../../../components/CustomTextField';
import PriorityIndicator from '../../PriorityIndicator';
import Colors from '../../../../../constants/Colors';
import AlertModal from '../../../../../components/AlertModal';
import BinIcon24 from '../../../../../components/24pxIcons/BinIcon24';
import Button from '../../../../../components/Button';
import { UserPermission } from '../../../../../constants/Types';

interface EditAreaModalProps {
  area: Area,
  // eslint-disable-next-line no-unused-vars
  updateArea: (area: Area) => void,
  removeArea: Function,
  editAreaModalShown: boolean,
  hideEditAreaModal: () => void,
  showScreenAlert: Function,
  parentName: string,
}

const EditAreaModal = ({
  area, updateArea, editAreaModalShown, hideEditAreaModal, showScreenAlert, removeArea, parentName,
}: EditAreaModalProps) => {
  if (!area) return null;

  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canDeleteLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_DELETE);
  const [loading, setLoading] = useState<boolean>(false);
  const [name, setName] = useState<string>(area.Name.trim());
  const [capacity, setCapacity] = useState<number | null>(asNumber(area.LocationCapacity));
  const [externalId, setExternalId] = useState<string | null>(area.ExternalId?.trim());
  const [isHighPriority, setIsHighPriority] = useState<boolean>(area.IsHighPriority === true);
  const [capacityError, setCapacityError] = useState<string | null>(null);
  const [priorityDropdownShown, setPriorityDropdownShown] = useState<boolean>(false);
  const [alertTitle, setAlertTitle] = useState<string>('Error');
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>('');
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [nameInvalidMessage, setNameInvalidMessage] = useState<string | null>(null);
  const [externalIdInvalidMessage, setExternalIdInvalidMessage] = useState<string | null>(null);
  const [clientId, setClientId] = useState('hereworks');

  const disabled: boolean = !!nameInvalidMessage || !!externalIdInvalidMessage;

  useEffect(() => {
    if (!(name?.length > 0)) setNameInvalidMessage('Name cannot be empty');
    else if (name?.length > 50) setNameInvalidMessage('Name cannot exceed 50 characters');
    else if (!nameRegex.exp.test(name)) {
      setNameInvalidMessage(`Name${nameRegex.message}`);
    } else setNameInvalidMessage(null);
    if (externalId?.trim().length > 0 && !externalIdRegex.exp.test(externalId.trim())) {
      setExternalIdInvalidMessage(externalIdRegex.message);
    } else setExternalIdInvalidMessage(null);
  }, [name, externalId]);

  useEffect(() => {
    AsyncStorage.getItem('clientName').then((n) => {
      setClientId(n);
    });
  }, []);

  const updateCapacity = (text: string) => {
    const value: number | null = asNumber(text);
    if (text?.trim().length > 0 && !integerRegex.exp.test(text.trim())) {
      setCapacityError(`Capacity${integerRegex.message}`);
    } else if (area.LocationSubtype && !value) {
      setCapacityError(PROVIDE_CAPACITY);
    } else setCapacityError(null);
    setCapacity(value);
  };

  const showAlert = (
    title: string,
    message: string,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = '',
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setAlertVisible(true);
  };

  const saveError = (errorText?: string) => {
    setLoading(false);
    showAlert('Error', errorText ?? EDIT_AREA_FAILURE);
  };

  const deleteError = (errorText?: string) => {
    setLoading(false);
    showAlert('Error', errorText ?? DELETE_AREA_FAILURE);
  };

  const save: () => void = () => {
    if (disabled) return;
    setLoading(true);
    editArea({
      LocationId: area.LocationId,
      Area: {
        Name: name,
        Top: area.Top,
        Left: area.Left,
        Right: area.Right,
        Bottom: area.Bottom,
        Points: area.Points,
        Polygon: area.Polygon,
        Priority: isHighPriority === true,
        ParentId: area.ParentId,
        ExternalId: externalId,
        LocationCapacity: capacity,
      },
    }).then((res) => {
      if (res && !res.isError) {
        updateArea({
          ...area,
          Name: name,
          IsHighPriority: isHighPriority === true,
          ExternalId: externalId,
          LocationCapacity: capacity,
        });
        setLoading(false);
        hideEditAreaModal();
        showScreenAlert(
          `Updated ${name}`,
          <Text>
            {'Name and priority of the area '}
            <Text style={Style.alertBoldText}>{name}</Text>
            {' was successfully updated'}
          </Text>,
          false,
        );
      } else saveError(res?.errorText);
    }).catch((error) => saveError(error?.message));
  };

  const remove: () => void = () => showAlert(
    `Delete ${area.Name}?`,
    'Are you sure you wish to delete this area?',
    true,
    () => {
      setLoading(true);
      deleteArea(area.LocationId).then((res) => {
        if (res && !res.isError) {
          removeArea(area.LocationId);
          setLoading(false);
          hideEditAreaModal();
          showScreenAlert(
            `Deleted ${area.Name}`,
            <Text>
              <Text style={Style.alertBoldText}>{area.Name}</Text>
              {' was successfully deleted from '}
              <Text style={Style.alertBoldText}>{parentName}</Text>
            </Text>,
            false,
          );
        } else deleteError(res?.errorText);
      }).catch((error) => deleteError(error?.message));
    },
    'Delete',
  );

  const onModalHide: () => void = () => {
    setName(area.Name?.trim());
    setExternalId(area.ExternalId?.trim());
    setIsHighPriority(area.IsHighPriority === true);
    setCapacity(asNumber(area.LocationCapacity));
    setCapacityError(null);
    setNameInvalidMessage(null);
    setExternalIdInvalidMessage(null);
    setLoading(false);
  };

  return <Modal
    isVisible={editAreaModalShown}
    onBackButtonPress={hideEditAreaModal}
    onBackdropPress={hideEditAreaModal}
    onSwipeComplete={hideEditAreaModal}
    {...{ onModalHide }}
    onModalWillShow={onModalHide}
    style={Style.modal}
  >
    <Pressable onPress={() => setPriorityDropdownShown(false)}>
      <View style={[Style.modalContent, styles.container]}>
        <PressableStyled
          style={Style.closeButton}
          onPress={hideEditAreaModal}>
          <CloseModalIcon />
        </PressableStyled>
        <Text style={styles.title}>{'Edit area'}</Text>
        <Text style={[styles.label, styles.subTitle]}>{'Fields marked with * are mandatory'}</Text>
        <View style={styles.textFieldContainer}>
          <CustomTextField
            title='Name'
            value={name}
            maxLength={50}
            updateValue={setName}
            placeholder='Enter area name'
            invalid={!!nameInvalidMessage}
            errorText={nameInvalidMessage}
            required
          />
          <PriorityIndicator
            style={styles.priorityIndicator}
            {...{
              priorityDropdownShown, setPriorityDropdownShown, isHighPriority, setIsHighPriority,
            }}
          />
        </View>
        {area.LocationSubtype?.length > 0
          && LocationSubtypeTitles[area.LocationSubtype]?.length > 0 && <>
            <Text style={styles.label}>{'Location type'}</Text>
            <View style={styles.locationType}>
              <Text style={styles.locationSubtypeText}>
                {LocationSubtypeTitles[area.LocationSubtype]}
              </Text>
            </View>
          </>}
        {!!area.LocationSubtype && <CustomTextField
          title='Capacity'
          maxLength={3}
          invalid={!!capacityError}
          errorText={capacityError}
          value={capacity?.toString()}
          updateValue={updateCapacity}
          placeholder='Enter capacity'
        />}
        <CustomTextField
          title='External ID'
          value={externalId}
          maxLength={50}
          updateValue={setExternalId}
          placeholder='Enter external ID'
          invalid={!!externalIdInvalidMessage}
          errorText={externalIdInvalidMessage}
          editable={clientId !== 'lazard' || area.ExternalId === null}
        />
        {(clientId === 'lazard' && area.ExternalId) && <Text style={{ color: Colors.light.hereworksUrgentRed }}>Please contact support to change an existing External ID</Text>}
        <View style={[Style.row, styles.buttonContainer]}>
          {canDeleteLocations ? <PressableStyled
            style={styles.deleteButton}
            onPress={remove}
            testID='edit-modal-delete-btn'
          >
            <BinIcon24 color={styles.deleteButton.borderColor} />
          </PressableStyled> : <View />}
          <View style={Style.row}>
            <Button
              title='Cancel'
              onPress={hideEditAreaModal}
              testID='edit-modal-cancel-btn'
            />
            <Button
              affirmative
              title='Save'
              {...{ disabled }}
              onPress={save}
              testID='edit-modal-save-btn'
            />
          </View>
        </View>
        <ActivityIndicator
          size='large'
          style={Style.activityIndicator}
          animating={loading}
        />
      </View>
    </Pressable>
    <AlertModal
      title={alertTitle}
      message={alertMessage}
      error={alertError}
      action={alertAction}
      actionTitle={alertActionTitle}
      modalVisible={alertVisible}
      setModalVisible={setAlertVisible}
    />
  </Modal>;
};

const styles = StyleSheet.create({
  container: {
    padding: 40,
    paddingTop: 60,
    alignSelf: 'center',
    width: 532,
  },
  title: {
    fontSize: 28,
    lineHeight: 35,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    alignSelf: 'center',
    textAlign: 'center',
    marginBottom: 12,
  },
  label: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginBottom: 4,
  },
  subTitle: {
    marginBottom: 28,
    alignSelf: 'center',
    textAlign: 'center',
  },
  textFieldContainer: {
    zIndex: 2,
    marginBottom: 12,
  },
  priorityIndicator: {
    position: 'absolute',
    end: 10,
    top: 50,
  },
  buttonContainer: {
    marginTop: 40,
  },
  deleteButton: {
    borderWidth: 1,
    borderRadius: 4,
    borderColor: Colors.light.hereworksRed700,
    justifyContent: 'center',
    alignItems: 'center',
    width: 40,
    height: 40,
  },
  locationType: {
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: '#FFF',
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  locationSubtypeText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
});

export default EditAreaModal;
